import { CommandError } from '../errors.js';
import { required } from './types.js';
/**
 * Restore the project to a recorded point.
 *
 * `inverse: null`, and that is not a gap. Nothing in the catalogue inverts to a restore, and the way back
 * from one is another restore rather than an undo — which works because `executeBatch` takes a point
 * immediately BEFORE any command that can refuse an inverse, and this is one of them. So a restore is
 * always chained-reversible: the point before it exists by construction, from the moment it runs.
 *
 * `touched: []`, and that IS deliberate. The `touched` vocabulary names individual things — `bucket:E4`,
 * `head:remain:G:…` — and a restore touches every one of them; there is no wildcard in that language and
 * inventing one would give `keysOverlap` a key it cannot compare. The hazard it would have guarded — an
 * older change being reverted against a project that has since been replaced — is answered exactly rather
 * than approximately by `RevertService`, which refuses to revert anything at or below the newest restore.
 */
export const restoreProject = {
    needs: ['project', 'restorePoints'],
    apply(slice, command) {
        const project = required(slice, 'project');
        const points = required(slice, 'restorePoints');
        if (command.confirmProjectName !== project.name) {
            throw new CommandError('invalid', 'Type the project name exactly to confirm restoring the whole project');
        }
        const point = points.find((p) => p.seq === command.seq);
        if (!point) {
            /*
              Named, not approximated. Offering "the nearest point before that one" would be a different act
              from the one that was asked for, and the caller cannot see that it happened.
            */
            throw new CommandError('not_found', points.length
                ? `There is no restore point at #${command.seq}. This project can be restored to `
                    + `${points.map((p) => `#${p.seq}`).join(', ')}.`
                : `There is no restore point at #${command.seq}, and this project has none at all.`);
        }
        return {
            writes: [{ t: 'restore', seq: command.seq }],
            inverse: null,
            summary: `Restored the project to #${point.seq} (${describe(point.reason)})`,
            touched: [],
        };
    },
};
/**
 * The point's `reason` in words, because the raw value is a storage detail.
 *
 * A gap in the log reads very differently depending on why the point exists: one taken deliberately
 * before a roster refresh is a rescue, one from the cadence is just where the clock landed. Unknown
 * reasons pass through rather than being swallowed — a future writer adding a reason should see it in the
 * summary, not have it silently rendered as something else.
 */
function describe(reason) {
    switch (reason) {
        case 'import':
            return 'the state it was imported with';
        case 'pre-destructive':
            return 'taken before a change that could not be undone';
        case 'cadence':
            return 'an automatic checkpoint';
        case 'backfill':
            return 'where the project stood when restore points began';
        default:
            return reason;
    }
}
//# sourceMappingURL=restore.js.map